import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	TextField,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Avatar,
	Typography,
	CircularProgress,
	Alert,
	Paper,
	Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { getAutocompletePassenger } from 'features/salesOrder/services';

interface SearchPassengerProps {
	onSelectPassenger: (passengerId: string) => void;
	isPhysicalPerson: boolean;
	placeholder?: string;
	existingPassengers?: Array<{
		keyId?: string;
		name: string;
		lastName: string;
		birthdate?: string;
		passengerId?: string;
	}>;
	onDuplicateError?: (message: string) => void;
}

interface PassengerResult {
	passengerId: string;
	name: string;
	lastName: string;
	email: string;
	phone: string;
	birthdate: string;
	isPhysicalPerson: boolean;
	documents: Array<{
		documentType: string;
		documentNumber: string;
	}>;
}

const SearchPassenger: React.FC<SearchPassengerProps> = ({
	onSelectPassenger,
	isPhysicalPerson,
	placeholder = 'Buscar pasajero existente...',
	existingPassengers = [],
	onDuplicateError,
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [results, setResults] = useState<PassengerResult[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [duplicateError, setDuplicateError] = useState<string | null>(null);
	const [showMinCharMessage, setShowMinCharMessage] = useState(false);

	const isPassengerAlreadyAdded = useCallback(
		(passenger: PassengerResult): boolean => {
			return existingPassengers.some((existing) => {
				if (existing.passengerId && passenger.passengerId === existing.passengerId) {
					return true;
				}

				if (existing.keyId && passenger.passengerId === existing.keyId) {
					return true;
				}

				const sameNameAndLastName =
					existing.name?.toLowerCase().trim() === passenger.name?.toLowerCase().trim() &&
					existing.lastName?.toLowerCase().trim() === passenger.lastName?.toLowerCase().trim();

				if (sameNameAndLastName && existing.birthdate && passenger.birthdate) {
					const existingDate = new Date(existing.birthdate).toDateString();
					const passengerDate = new Date(passenger.birthdate).toDateString();
					return existingDate === passengerDate;
				}

				return false;
			});
		},
		[existingPassengers],
	);

	const performSearch = useCallback(
		async (term: string) => {
			if (!term.trim() || term.length < 4) {
				setResults([]);
				setDuplicateError(null);
				setError(null);
				setShowMinCharMessage(term.length > 0 && term.length < 4);
				return;
			}

			setShowMinCharMessage(false);
			setLoading(true);
			setError(null);
			setDuplicateError(null);

			try {
				const { data } = await getAutocompletePassenger(term);

				const mappedResults: PassengerResult[] = data
					.filter((passenger: any) => passenger.isPhysicalPerson === isPhysicalPerson)
					.map((passenger: any) => ({
						passengerId: passenger.passengerId,
						name: passenger.name,
						lastName: passenger.lastName,
						email: passenger.email || '',
						phone: (passenger as any).phone || '',
						birthdate: passenger.birthdate,
						isPhysicalPerson: passenger.isPhysicalPerson,
						documents: passenger.documents || [],
					}));

				setResults(mappedResults);
			} catch (err) {
				console.error('Error searching passengers:', err);
				setError('Error al buscar pasajeros. Intenta nuevamente.');
				setResults([]);
			} finally {
				setLoading(false);
			}
		},
		[isPhysicalPerson],
	);

	React.useEffect(() => {
		const timeoutId = setTimeout(() => {
			performSearch(searchTerm);
		}, 300);

		return () => clearTimeout(timeoutId);
	}, [searchTerm, performSearch]);

	const handleSelectPassenger = (passengerId: string, passenger: PassengerResult) => {
		if (isPassengerAlreadyAdded(passenger)) {
			const errorMessage = `${passenger.name} ${passenger.lastName} ya está agregado a esta orden`;
			setDuplicateError(errorMessage);

			if (onDuplicateError) {
				onDuplicateError(errorMessage);
			}

			return;
		}

		setDuplicateError(null);
		setShowMinCharMessage(false);
		onSelectPassenger(passengerId);
		setSearchTerm('');
		setResults([]);
	};

	const filteredResults = useMemo(() => {
		const filtered = results
			.filter((passenger) => passenger.isPhysicalPerson === isPhysicalPerson)
			.filter((passenger) => !isPassengerAlreadyAdded(passenger));

		return filtered;
	}, [results, isPhysicalPerson, isPassengerAlreadyAdded]);

	const duplicatesFiltered = useMemo(() => {
		const totalResults = results.filter((passenger) => passenger.isPhysicalPerson === isPhysicalPerson);
		return totalResults.length - filteredResults.length;
	}, [results, filteredResults, isPhysicalPerson]);

	return (
		<Box>
			<TextField
				fullWidth
				variant="outlined"
				placeholder={placeholder}
				value={searchTerm}
				onChange={(e) => {
					setSearchTerm(e.target.value);
					setDuplicateError(null);
					setError(null);
				}}
				size="small"
				sx={{ mb: 1 }}
			/>

			{loading && (
				<Box display="flex" justifyContent="center" p={2}>
					<CircularProgress size={24} />
				</Box>
			)}

			{showMinCharMessage && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Ingresa al menos 4 caracteres para buscar un pasajero
				</Alert>
			)}

			{error && !showMinCharMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{duplicateError && !showMinCharMessage && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					{duplicateError}
				</Alert>
			)}

			{filteredResults.length > 0 && (
				<Paper elevation={2} sx={{ maxHeight: 300, overflow: 'auto' }}>
					<List dense>
						{filteredResults.map((passenger) => (
							<ListItem
								key={passenger.passengerId}
								button
								onClick={() => handleSelectPassenger(passenger.passengerId, passenger)}
								sx={{
									'&:hover': {
										backgroundColor: 'action.hover',
									},
								}}
							>
								<ListItemAvatar>
									<Avatar
										sx={{
											bgcolor: passenger.isPhysicalPerson ? 'primary.main' : 'secondary.main',
											width: 32,
											height: 32,
										}}
									>
										{passenger.isPhysicalPerson ? <PersonIcon /> : <BusinessIcon />}
									</Avatar>
								</ListItemAvatar>

								<ListItemText
									primary={
										<Box display="flex" alignItems="center" gap={1}>
											<Typography variant="subtitle2" fontWeight={600}>
												{passenger.name} {passenger.lastName}
											</Typography>
											{passenger.documents.length > 0 && (
												<Chip
													label={`${passenger.documents.length} doc${passenger.documents.length !== 1 ? 's' : ''}`}
													size="small"
													variant="outlined"
													color="primary"
												/>
											)}
										</Box>
									}
									secondary={
										<Box>
											{passenger.birthdate && (
												<Typography variant="caption" color="text.secondary">
													📅 {new Date(passenger.birthdate).toLocaleDateString()}
												</Typography>
											)}
											<Box display="flex" gap={2} mt={0.5}>
												{passenger.email && (
													<Typography variant="caption" color="text.secondary">
														📧 {passenger.email}
													</Typography>
												)}
												{passenger.phone && (
													<Typography variant="caption" color="text.secondary">
														📞 {passenger.phone}
													</Typography>
												)}
											</Box>
										</Box>
									}
								/>
							</ListItem>
						))}
					</List>
				</Paper>
			)}

			{duplicatesFiltered > 0 && searchTerm.length >= 4 && !loading && !showMinCharMessage && (
				<Alert severity="info" sx={{ mt: 2 }}>
					{duplicatesFiltered} {isPhysicalPerson ? 'persona' : 'empresa'}
					{duplicatesFiltered !== 1 ? 's' : ''} ya
					{duplicatesFiltered !== 1 ? ' están agregadas' : ' está agregada'} a esta orden y fueron excluidas de los
					resultados.
				</Alert>
			)}

			{searchTerm.length >= 4 &&
				!loading &&
				filteredResults.length === 0 &&
				duplicatesFiltered === 0 &&
				!showMinCharMessage && (
					<Alert severity="info" sx={{ mt: 2 }}>
						No se encontraron {isPhysicalPerson ? 'personas físicas' : 'personas jurídicas'} que coincidan con &quot;
						{searchTerm}&quot;
					</Alert>
				)}

			{searchTerm.length >= 4 &&
				!loading &&
				filteredResults.length === 0 &&
				duplicatesFiltered > 0 &&
				!showMinCharMessage && (
					<Alert severity="info" sx={{ mt: 2 }}>
						Todos los resultados para &quot;{searchTerm}&quot; ya están agregados a esta orden.
					</Alert>
				)}
		</Box>
	);
};

SearchPassenger.propTypes = {
	onSelectPassenger: PropTypes.func.isRequired,
	isPhysicalPerson: PropTypes.bool.isRequired,
	placeholder: PropTypes.string,
	existingPassengers: PropTypes.array,
	onDuplicateError: PropTypes.func,
};

export default SearchPassenger;
